import React, { useEffect, useState, useMemo } from 'react';
import { useParams, useNavigate, Link as RouterLink } from 'react-router-dom';
import { isMobile } from 'react-device-detect';
import {
   Container,
   Content,
   FlexboxGrid,
   Panel,
   Button,
   Badge,
   Loader,
   Divider,
   Message,
   useToaster
 } from 'rsuite';

import CardStat from './CardStat';
import Link from '../Shared/Link';
import NavigationBar from '../Shared/NavigationBar';
import SearchBar from '../Shared/SearchBar';
import AddToCollectionModal from '../Collections/AddToCollectionModal';
import CardLocationModal from '../Collections/CardLocationModal';
import { useAuth } from '../Auth/AuthContext';
import { KEYWORD_GLOSSARY } from '../../helpers/rulesData';
import { usePreferences } from '../Preferences/PreferencesContext';
import { getThemeColors } from '../../helpers/themeColors';
import { API_BASE } from '../../helpers/authFetch';
import { SCRYFALL_ID_DRAG_TYPE } from '../../helpers/dragDropCardParser';

/**
 * Check if a card is double-faced (transform, modal DFC, etc.)
 * Double-faced cards have card_faces array but no top-level image_uris
 */
const isDoubleFaced = (card) => card?.card_faces && !card?.image_uris;

/**
 * Get the image URI for a card, handling double-faced cards
 * @param {object} card - The card object from Scryfall
 * @param {number} faceIndex - Which face to get (0 = front, 1 = back)
 * @returns {string|null} The image URI or null
 */
const getImageUri = (card, faceIndex = 0) => {
  if (card?.image_uris?.normal) {
    return card.image_uris.normal;
  }
  if (card?.card_faces?.[faceIndex]?.image_uris?.normal) {
    return card.card_faces[faceIndex].image_uris.normal;
  }
  return null;
};

/**
 * Get oracle text for a card, handling double-faced cards
 * For double-faced cards, returns formatted text with face names as labels
 * @param {object} card - The card object from Scryfall
 * @returns {string|null} The oracle text or null
 */
const getOracleText = (card) => {
  if (card?.oracle_text) {
    return card.oracle_text;
  }
  if (card?.card_faces) {
    return card.card_faces
      .map(face => `${face.name}:\n${face.oracle_text || ''}`)
      .join('\n\n');
  }
  return null;
};

/**
 * Get the current face data for a card
 * For double-faced cards, returns the face at the given index
 * For normal cards, returns the card itself
 */
const getCurrentFace = (card, faceIndex = 0) => {
  if (isDoubleFaced(card)) {
    return card.card_faces[faceIndex];
  }
  return card;
};

/**
 * Get power for a card, handling double-faced cards
 */
const getPower = (card, faceIndex = 0) => {
  const face = getCurrentFace(card, faceIndex);
  return face?.power || null;
};

/**
 * Get toughness for a card, handling double-faced cards
 */
const getToughness = (card, faceIndex = 0) => {
  const face = getCurrentFace(card, faceIndex);
  return face?.toughness || null;
};

/**
 * Get loyalty for a card, handling double-faced cards
 */
const getLoyalty = (card, faceIndex = 0) => {
  const face = getCurrentFace(card, faceIndex);
  return face?.loyalty || null;
};

const RULINGS_PANEL_MARGIN_TOP = 20;
const RULINGS_HEADER_GAP = 8;
const RULINGS_META_FONT_SIZE = 12;
const RULINGS_ITEM_MARGIN_BOTTOM = 4;
const RULINGS_DIVIDER_MARGIN = '8px 0';

const PRINTINGS_TEXT_LIST_ROW_PADDING = '12px';
const PRINTINGS_TEXT_LIST_ROW_MARGIN = '8px 0';
const PRINTINGS_TEXT_LIST_ROW_BORDER_RADIUS = '4px';
const PRINTINGS_TEXT_LIST_ROW_HOVER_BG = '#2a2a4a';
const PRINTINGS_TEXT_LIST_CODE_COLOR = '#888';
const PRINTINGS_TEXT_LIST_FONT_SIZE = '14px';

const RULES_SECTION_MARGIN_TOP = 16;
const RULES_TAG_PADDING = '4px 10px';
const RULES_TAG_BORDER_RADIUS = 4;
const RULES_TAG_FONT_SIZE = 12;
const RULES_TAG_FONT_WEIGHT = 500;
const RULES_TAG_GAP = 6;
const RULES_HEADING_FONT_SIZE = 13;
const RULES_HEADING_MARGIN_BOTTOM = 8;
const RULES_HEADING_LETTER_SPACING = 0.5;

const INLINE_SEARCH_SPACING = {
  paddingHorizontal: 15,
  marginBottom: 15,
};

const TRIGGERED_ABILITY_REGEX = /\b(When|Whenever|At the beginning of)\b/i;
const COMBAT_KEYWORDS = ['First Strike', 'Double Strike', 'Trample', 'Menace', 'Flanking', 'Bushido', 'Ninjutsu'];

const getKeywordNames = () => KEYWORD_GLOSSARY.map((kw) => kw.name);

const findCardKeywords = (card) => {
  if (!card) return [];
  const oracleText = getOracleText(card) || '';
  const keywordList = card.keywords || [];
  const keywordNames = getKeywordNames();
  const matched = new Set();

  keywordList.forEach((kw) => {
    if (keywordNames.includes(kw)) matched.add(kw);
  });

  keywordNames.forEach((kw) => {
    const regex = new RegExp(`\\b${kw.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\b`, 'i');
    if (regex.test(oracleText)) matched.add(kw);
  });

  return Array.from(matched).sort();
};

const getContextualLinks = (card) => {
  if (!card) return [];
  const links = [];
  const oracleText = getOracleText(card) || '';
  const keywords = findCardKeywords(card);

  if (keywords.some((kw) => COMBAT_KEYWORDS.includes(kw))) {
    links.push({ label: '⚔️ Combat Rules', to: '/rules/combat' });
  }

  const typeLine = card.type_line || (card.card_faces && card.card_faces.map((f) => f.type_line).join(' ')) || '';
  if (typeLine.toLowerCase().includes('planeswalker')) {
    links.push({ label: '🛡️ Planeswalker Rules', to: '/rules/stack-and-priority' });
  }

  if (TRIGGERED_ABILITY_REGEX.test(oracleText)) {
    links.push({ label: '📚 Stack & Priority', to: '/rules/stack-and-priority' });
  }

  return links;
};

const DESKTOP_IMAGE_WIDTH = 340;
const DESKTOP_IMAGE_HEIGHT = 475;

const MOBILE_IMAGE_MAX_WIDTH = '100%';
const MOBILE_IMAGE_MARGIN_BOTTOM = 20;

const MOBILE_BUTTON_WIDTH = '100%';

const MOBILE_RULINGS_META_FONT_SIZE = 13;
const MOBILE_RULINGS_COMMENT_LINE_HEIGHT = 1.6;
const MOBILE_RULINGS_ITEM_MARGIN_BOTTOM = 8;

const CardView = () => {
   const {id} = useParams();
   const navigate = useNavigate();
   const [card, setCard] =  useState();
   const [activeFace, setActiveFace] = useState(0);
   const [showAddModal, setShowAddModal] = useState(false);
   const [showLocationsModal, setShowLocationsModal] = useState(false);
   const { preferences } = usePreferences();
   const themeColors = getThemeColors(preferences.theme);
   const RULES_TAG_BG = preferences.theme === 'light' ? '#e3f2fd' : '#1e2a4a';
   const RULES_TAG_COLOR = themeColors.accentPrimary;
   const RULES_HEADING_COLOR = themeColors.textFaint;
   const [cardError, setCardError] = useState(false);
   const [rulings, setRulings] = useState([]);
   const [rulingsLoading, setRulingsLoading] = useState(false);
   const [rulingsError, setRulingsError] = useState(false);
   const [printings, setPrintings] = useState([]);
   const [printingsLoading, setPrintingsLoading] = useState(false);
   const [printingsError, setPrintingsError] = useState(false);
   const [priceRefreshing, setPriceRefreshing] = useState(false);
   const [inlineCardSearch, setInlineCardSearch] = useState('');
   const [expandedGroups, setExpandedGroups] = useState(() => {
     try {
       const saved = localStorage.getItem('printingGroupsExpanded');
       return saved ? JSON.parse(saved) : {};
     } catch {
       return {};
     }
   });

   useEffect(() => {
     localStorage.setItem('printingGroupsExpanded', JSON.stringify(expandedGroups));
   }, [expandedGroups]);
   const { isAuthenticated } = useAuth();
   const toaster = useToaster();

   const matchedKeywords = useMemo(() => findCardKeywords(card), [card]);
   const contextualLinks = useMemo(() => getContextualLinks(card), [card]);
   const hasRulesLinks = matchedKeywords.length > 0 || contextualLinks.length > 0;

   const sortedPrintings = useMemo(() => {
     return [...printings].sort((a, b) => {
       if (!a.released_at) return 1;
       if (!b.released_at) return -1;
       const dateA = new Date(a.released_at);
       const dateB = new Date(b.released_at);
       return dateB - dateA;
     });
   }, [printings]);

   const groupedPrintings = useMemo(() => {
     const groups = {};
     sortedPrintings.forEach(printing => {
       const setName = printing.set_name || 'Unknown';
       if (!groups[setName]) {
         groups[setName] = {
           setName,
           released_at: printing.released_at,
           printings: []
         };
       }
       groups[setName].printings.push(printing);
     });
     return Object.values(groups)
       .map(group => ({
         ...group,
         printings: [...group.printings].sort((a, b) => {
           if (!a.released_at) return 1;
           if (!b.released_at) return -1;
           const dateA = new Date(a.released_at);
           const dateB = new Date(b.released_at);
           return dateB - dateA;
         })
       }))
       .sort((a, b) => {
         if (!a.released_at) return 1;
         if (!b.released_at) return -1;
         const dateA = new Date(a.released_at);
         const dateB = new Date(b.released_at);
         return dateB - dateA;
       });
   }, [sortedPrintings]);

   const toggleFace = () => {
    setActiveFace(prev => prev === 0 ? 1 : 0);
  };

  const handleCardImageDragStart = (e) => {
    const imageUrl = getImageUri(card, activeFace);
    if (card?.id) {
      e.dataTransfer.setData(SCRYFALL_ID_DRAG_TYPE, card.id);
    }
    if (imageUrl) {
      e.dataTransfer.setData('text/uri-list', imageUrl);
      e.dataTransfer.setData('text/plain', imageUrl);
    }
    e.dataTransfer.effectAllowed = 'copy';
  };

  const handleRefreshPrice = async () => {
    if (!card?.id) return;
    setPriceRefreshing(true);
    try {
      const res = await fetch(`${API_BASE}/cards/${card.id}/refresh`, { method: 'POST' });
      if (res.ok) {
        const updatedCard = await res.json();
        setCard(updatedCard);
        toaster.push(
          <Message type="success" showIcon closable>Price refreshed</Message>,
          { placement: 'topCenter', duration: 3000 }
        );
      } else if (res.status === 429) {
        const retryAfter = res.headers.get('Retry-After') || '1';
        const minutes = Math.ceil(parseInt(retryAfter, 10) / 60);
        toaster.push(
          <Message type="warning" showIcon closable>Price was refreshed recently. Try again in {minutes} minute{minutes !== 1 ? 's' : ''}.</Message>,
          { placement: 'topCenter', duration: 4000 }
        );
      } else {
        throw new Error('Failed to refresh price');
      }
    } catch (error) {
      toaster.push(
        <Message type="error" showIcon closable>Failed to refresh price</Message>,
        { placement: 'topCenter', duration: 4000 }
      );
    } finally {
      setPriceRefreshing(false);
    }
  };

  const searchCards = async (searchTerm) => {
    const controller = new AbortController();
    try {
      const results = await fetch(`${API_BASE}/cards/search/${encodeURIComponent(searchTerm)}`, {
        signal: controller.signal,
      });
      if (!results.ok) {
        throw new Error(`HTTP ${results.status}: Failed to search cards`);
      }
      const data = await results.text();
      return data;
    } catch (error) {
      if (error.name === 'AbortError') {
        return null;
      }
      console.error('Card search error:', error);
      throw error;
    }
  };

  const handleCardSelect = (selectedCard) => {
    if (selectedCard && selectedCard.id) {
      navigate(`/cardsearch/${selectedCard.id}`);
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const cardData = await fetch(`${API_BASE}/cards/${id}`);
        if (!cardData.ok) throw new Error('Failed to fetch card');
        const cardJSON = await cardData.json();
        setCard(cardJSON);
      } catch {
        setCardError(true);
      }
    };

    fetchData();
  }, [id]);

  useEffect(() => {
    if (!card?.id) return;
    const fetchRulings = async () => {
      setRulingsLoading(true);
      setRulingsError(false);
      try {
        const res = await fetch(`${API_BASE}/cards/${card.id}/rulings`);
        if (!res.ok) throw new Error('Failed to fetch rulings');
        const data = await res.json();
        const sorted = [...data].sort((a, b) => new Date(b.published_at) - new Date(a.published_at));
        setRulings(sorted);
      } catch {
        setRulingsError(true);
      } finally {
        setRulingsLoading(false);
      }
    };
    fetchRulings();
  }, [card?.id]);

  useEffect(() => {
    if (!card?.id) return;
    const fetchPrintings = async () => {
      setPrintingsLoading(true);
      setPrintingsError(false);
      try {
        const res = await fetch(`${API_BASE}/cards/${card.id}/printings`);
        if (!res.ok) throw new Error('Failed to fetch printings');
        const data = await res.json();
        setPrintings(data);
      } catch {
        setPrintingsError(true);
      } finally {
        setPrintingsLoading(false);
      }
    };
    fetchPrintings();
  }, [card?.id]);

  useEffect(() => {
    setInlineCardSearch('');
  }, [id]);

  useEffect(() => {
    if (isDoubleFaced(card)) {
      const otherFace = activeFace === 0 ? 1 : 0;
      const otherUri = getImageUri(card, otherFace);
      if (otherUri) {
        const img = new Image();
        img.src = otherUri;
      }
    }
  }, [card, activeFace]);

  return (
    <>
      <Container>
        <NavigationBar />
          <Content style={{ marginTop: "15px" }}>
            <div style={{ marginBottom: `${INLINE_SEARCH_SPACING.marginBottom}px`, paddingLeft: `${INLINE_SEARCH_SPACING.paddingHorizontal}px`, paddingRight: `${INLINE_SEARCH_SPACING.paddingHorizontal}px` }}>
              <SearchBar
                term={inlineCardSearch}
                setTerm={setInlineCardSearch}
                retrieve={searchCards}
                onSelect={handleCardSelect}
                placeholder="Search for another card..."
              />
            </div>
            {cardError && (
              <p style={{ color: '#aaa', textAlign: 'center', marginTop: 40 }}>
                Unable to load card. Please try again.
              </p>
            )}
            {!cardError && isMobile && (
              <div style={{ marginBottom: MOBILE_IMAGE_MARGIN_BOTTOM, textAlign: 'center' }}>
                {getImageUri(card, activeFace) && (
                  <img
                    src={getImageUri(card, activeFace)}
                    alt={card?.name || 'Card image'}
                    style={{
                      maxWidth: MOBILE_IMAGE_MAX_WIDTH,
                      height: 'auto',
                      decoding: 'async',
                    }}
                    draggable
                    onDragStart={handleCardImageDragStart}
                  />
                )}
                {isDoubleFaced(card) && (
                  <Button 
                    appearance="primary" 
                    onClick={toggleFace}
                    style={{ marginTop: MOBILE_IMAGE_MARGIN_BOTTOM, width: MOBILE_BUTTON_WIDTH }}
                  >
                    {activeFace === 0 ? 'Show Back Face' : 'Show Front Face'}
                  </Button>
                )}
              </div>
            )}
            {!cardError && <FlexboxGrid justify="center" align="top">
              <FlexboxGrid.Item colspan={isMobile ? 20 : 14} style={{ paddingRight: isMobile ? "0px" : "10px" }}>
                <Panel bordered>
                  <h2 style={{ marginBottom: "25px"}}>{card?.name}</h2>
                  {getPower(card, activeFace) && 
                    <>
                      <CardStat title="Power" value={getPower(card, activeFace)} />
                      <CardStat title="Toughness" value={getToughness(card, activeFace)} />
                    </>
                  }
                  {getLoyalty(card, activeFace) &&
                    <CardStat title="Loyalty" value={getLoyalty(card, activeFace)} />
                  }
                  <CardStat title="Approximate Cost" value={"$" + card?.prices?.usd} />
                  {card?.prices.usd_foil &&
                    <CardStat title="Approximate Foil Cost" value={"$" + card?.prices?.usd_foil} />
                  }
                  <CardStat title="Set" value={card?.set_name} />
                  <CardStat title="Commander Legality" value={card?.legalities?.commander === "legal" ? "Legal" : "Not Legal"} />
                  <CardStat title="Standard Legality" value={card?.legalities?.standard === "legal" ? "Legal" : "Not Legal"} />
                  <CardStat title="Ability" value={getOracleText(card)} />
                  {hasRulesLinks && (
                    <div style={{ marginTop: RULES_SECTION_MARGIN_TOP }}>
                      <div style={{
                        fontSize: RULES_HEADING_FONT_SIZE,
                        color: RULES_HEADING_COLOR,
                        marginBottom: RULES_HEADING_MARGIN_BOTTOM,
                        textTransform: 'uppercase',
                        letterSpacing: RULES_HEADING_LETTER_SPACING,
                      }}>
                        Related Rules
                      </div>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: RULES_TAG_GAP }}>
                        {matchedKeywords.map((kw) => (
                          <RouterLink
                            key={kw}
                            to={`/rules/keywords?q=${encodeURIComponent(kw)}`}
                            style={{
                              backgroundColor: RULES_TAG_BG,
                              color: RULES_TAG_COLOR,
                              padding: RULES_TAG_PADDING,
                              borderRadius: RULES_TAG_BORDER_RADIUS,
                              fontSize: RULES_TAG_FONT_SIZE,
                              fontWeight: RULES_TAG_FONT_WEIGHT,
                              textDecoration: 'none',
                            }}
                          >
                            {kw}
                          </RouterLink>
                        ))}
                        {contextualLinks.map((link) => (
                          <RouterLink
                            key={link.to}
                            to={link.to}
                            style={{
                              backgroundColor: RULES_TAG_BG,
                              color: RULES_TAG_COLOR,
                              padding: RULES_TAG_PADDING,
                              borderRadius: RULES_TAG_BORDER_RADIUS,
                              fontSize: RULES_TAG_FONT_SIZE,
                              fontWeight: RULES_TAG_FONT_WEIGHT,
                              textDecoration: 'none',
                            }}
                          >
                            {link.label}
                          </RouterLink>
                        ))}
                      </div>
                    </div>
                  )}
                  <h5 style={{marginTop: "25px"}}>Purchase Links</h5>
                  <Link url={card?.purchase_uris?.tcgplayer} title="TCG Player" />
                  <Link url={card?.purchase_uris?.cardmarket} title="Card Market" />
                  <Panel
                    collapsible
                    defaultExpanded
                    bordered
                    header={
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: RULINGS_HEADER_GAP }}>
                        Rulings
                        {rulings.length > 0 && <Badge content={rulings.length} />}
                      </span>
                    }
                    style={{ marginTop: RULINGS_PANEL_MARGIN_TOP }}
                  >
                    {rulingsLoading && <Loader content="Loading rulings..." />}
                    {rulingsError && <p style={{ color: '#aaa' }}>Unable to load rulings.</p>}
                    {!rulingsLoading && !rulingsError && rulings.length === 0 && (
                      <p style={{ color: '#aaa' }}>No rulings available for this card.</p>
                    )}
                    {!rulingsLoading && !rulingsError && rulings.map((ruling, i) => (
                      <div key={i}>
                        <p style={{ whiteSpace: 'pre-wrap', marginBottom: isMobile ? MOBILE_RULINGS_ITEM_MARGIN_BOTTOM : RULINGS_ITEM_MARGIN_BOTTOM, lineHeight: isMobile ? MOBILE_RULINGS_COMMENT_LINE_HEIGHT : 1.5 }}>{ruling.comment}</p>
                        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: isMobile ? MOBILE_RULINGS_META_FONT_SIZE : RULINGS_META_FONT_SIZE, color: '#888', marginBottom: isMobile ? MOBILE_RULINGS_ITEM_MARGIN_BOTTOM : RULINGS_ITEM_MARGIN_BOTTOM }}>
                          <span>{new Date(ruling.published_at + 'T00:00:00').toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</span>
                          <span>{ruling.source === 'wotc' ? 'WotC' : 'Scryfall'}</span>
                        </div>
                        {i < rulings.length - 1 && <Divider style={{ margin: RULINGS_DIVIDER_MARGIN }} />}
                      </div>
                    ))}
                  </Panel>

                  {printingsLoading && (
                    <Panel
                      bordered
                      style={{ marginTop: RULINGS_PANEL_MARGIN_TOP }}
                    >
                      <Loader content="Loading printings..." />
                    </Panel>
                  )}

                  {printingsError && (
                    <Panel
                      bordered
                      style={{ marginTop: RULINGS_PANEL_MARGIN_TOP }}
                    >
                      <p style={{ color: '#aaa' }}>Unable to load printings.</p>
                    </Panel>
                  )}

                  {!printingsLoading && !printingsError && printings.length > 1 && (
                    <Panel
                      collapsible
                      defaultExpanded
                      bordered
                      header={
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: RULINGS_HEADER_GAP }}>
                          All Printings
                          {printings.length > 1 && <Badge content={printings.length} />}
                        </span>
                      }
                      style={{ marginTop: RULINGS_PANEL_MARGIN_TOP }}
                    >
                      <div>
                        {groupedPrintings.map((group, index) => {
                          const year = (() => {
                            if (!group.released_at) return 'N/A';
                            const date = new Date(group.released_at);
                            return isNaN(date.getTime()) ? 'N/A' : date.getFullYear();
                          })();
                          const printingCount = group.printings.length;
                          const groupKey = `${group.setName}|${group.released_at || 'unknown'}`;
                          const isExpanded = expandedGroups[group.setName] !== false;
                          const toggleExpanded = () => {
                            setExpandedGroups(prev => ({
                              ...prev,
                              [group.setName]: !isExpanded
                            }));
                          };
                          return (
                            <div key={groupKey} style={{ marginBottom: '12px' }}>
                              <div
                                role="button"
                                tabIndex={0}
                                onClick={toggleExpanded}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter' || e.key === ' ') {
                                    e.preventDefault();
                                    toggleExpanded();
                                  }
                                }}
                                style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '8px',
                                  padding: '8px 0',
                                  cursor: 'pointer',
                                  userSelect: 'none',
                                  fontWeight: 600,
                                  fontSize: '14px',
                                }}
                                aria-expanded={isExpanded}
                                aria-label={`${isExpanded ? 'Collapse' : 'Expand'} ${group.setName} (${year})`}
                              >
                                <span style={{ display: 'inline-flex', alignItems: 'center', minWidth: '16px' }}>
                                  {isExpanded ? '▼' : '▶'}
                                </span>
                                <span>
                                  {group.setName} ({year})
                                </span>
                                {printingCount > 1 && (
                                  <Badge content={printingCount} />
                                )}
                              </div>
                              {isExpanded && (
                                <div style={{ paddingLeft: '16px' }}>
                                  {group.printings.map((printing) => {
                                    const printingYear = (() => {
                                      if (!printing.released_at) return 'N/A';
                                      const date = new Date(printing.released_at);
                                      return isNaN(date.getTime()) ? 'N/A' : date.getFullYear();
                                    })();
                                    const setCode = (typeof printing.set === 'string') ? printing.set.toUpperCase() : 'UNK';
                                    const imageSrc = printing.image_uris?.small;
                                    return (
                                      <div
                                        key={printing.id}
                                        role="button"
                                        tabIndex={0}
                                        onClick={() => navigate(`/cardsearch/${printing.id}`)}
                                        onKeyDown={(e) => {
                                          if (e.key === 'Enter' || e.key === ' ') {
                                            e.preventDefault();
                                            navigate(`/cardsearch/${printing.id}`);
                                          }
                                        }}
                                        style={{
                                          display: 'flex',
                                          alignItems: 'center',
                                          gap: '12px',
                                          padding: PRINTINGS_TEXT_LIST_ROW_PADDING,
                                          margin: PRINTINGS_TEXT_LIST_ROW_MARGIN,
                                          borderRadius: PRINTINGS_TEXT_LIST_ROW_BORDER_RADIUS,
                                          backgroundColor: printing.id === card?.id ? PRINTINGS_TEXT_LIST_ROW_HOVER_BG : 'transparent',
                                          cursor: 'pointer',
                                          transition: 'background-color 0.2s',
                                          fontSize: PRINTINGS_TEXT_LIST_FONT_SIZE,
                                        }}
                                        onMouseEnter={(e) => {
                                          e.currentTarget.style.backgroundColor = PRINTINGS_TEXT_LIST_ROW_HOVER_BG;
                                        }}
                                        onMouseLeave={(e) => {
                                          e.currentTarget.style.backgroundColor = printing.id === card?.id ? PRINTINGS_TEXT_LIST_ROW_HOVER_BG : 'transparent';
                                        }}
                                        aria-label={`View printing: ${printing.set_name || 'Unknown'} #${printing.collector_number || '?'}`}
                                      >
                                        {imageSrc && (
                                          <img
                                            src={imageSrc}
                                            alt={`${setCode} #${printing.collector_number}`}
                                            style={{
                                              height: '100px',
                                              borderRadius: '4px',
                                              flexShrink: 0,
                                              boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                                            }}
                                          />
                                        )}
                                        <div style={{ flex: 1 }}>
                                          <span style={{ fontWeight: 600 }}>
                                            [{setCode}] #{printing.collector_number || '?'}
                                          </span>
                                          <span style={{ color: PRINTINGS_TEXT_LIST_CODE_COLOR, marginLeft: '8px' }}>
                                            — {printing.set_name || 'Unknown'} ({printingYear})
                                          </span>
                                        </div>
                                      </div>
                                    );
                                  })}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </Panel>
                  )}

                  <div style={{ display: 'flex', gap: 10, marginTop: 20, flexDirection: isMobile ? 'column' : 'row' }}>
                    {isAuthenticated && (
                      <>
                        <Button
                          appearance="primary"
                          color="green"
                          block={isMobile}
                          style={{ flex: isMobile ? undefined : 1 }}
                          onClick={() => setShowAddModal(true)}
                        >
                          Add to Collection
                        </Button>
                        <Button
                          appearance="primary"
                          block={isMobile}
                          style={{ flex: isMobile ? undefined : 1 }}
                          onClick={() => setShowLocationsModal(true)}
                        >
                          View in Decks
                        </Button>
                      </>
                    )}
                    <Button
                      appearance="ghost"
                      block={isMobile}
                      style={{ flex: isMobile ? undefined : 1 }}
                      loading={priceRefreshing}
                      onClick={handleRefreshPrice}
                    >
                      Refresh Price
                    </Button>
                  </div>
                </Panel>
              </FlexboxGrid.Item>
              
            {!isMobile &&
              <FlexboxGrid.Item colspan={4}>
                <img
                  src={getImageUri(card, activeFace)}
                  alt={card?.name || 'Card image'}
                  width={DESKTOP_IMAGE_WIDTH}
                  height={DESKTOP_IMAGE_HEIGHT}
                  decoding="async"
                  draggable
                  onDragStart={handleCardImageDragStart}
                />
                {isDoubleFaced(card) && (
                  <Button 
                    appearance="primary" 
                    onClick={toggleFace}
                    style={{ marginTop: '10px', width: '100%' }}
                  >
                    {activeFace === 0 ? 'Show Back Face' : 'Show Front Face'}
                  </Button>
                )}
              </FlexboxGrid.Item>
            }
          </FlexboxGrid>}
          {isAuthenticated && card && (
            <>
              <AddToCollectionModal
                open={showAddModal}
                onClose={() => setShowAddModal(false)}
                scryfallId={card.id}
                cardName={card.name}
                card={card}
              />
              <CardLocationModal
                open={showLocationsModal}
                onClose={() => setShowLocationsModal(false)}
                scryfallId={card.id}
                cardName={card.name}
                cardObject={card}
              />
            </>
          )}
        </Content>
      </Container>
    </>
  );
}

export default CardView;